import { useCallback } from 'react';
import api from '../../../../services/api';
import { useRealTimeChat } from '../../../chat/context/RealTimeChatContext';

const isFlagOn = (value) => value === true || Number(value) === 1;

/** The answer of GET /ppt-config/rfp/{id}/all has ppt_generated: 0 (first time) or 1 (already generated). */
const isPresentationGenerated = (data) => isFlagOn(data?.ppt_generated) || isFlagOn(data?.slide_order?.ppt_generated);

/**
 * Makes sure the slides of an RFP exist before a PowerPoint is downloaded.
 *
 *   1. GET  /ppt-config/rfp/{rfp_id}/all             -> already generated? then nothing to do
 *   2. POST /ppt-config/rfp/{rfp_id}/generate-slides -> starts the generation
 *   3. waits for the websocket to report PPT_GENERATION_COMPLETED (or _FAILED), with no time limit
 *
 * The progress messages of step 3 are shown by useDocumentProgress (PPT_GENERATION_PROGRESS).
 *
 * @returns {{ ensureSlidesGenerated: (options: { rfpId: string, templateId?: string }) => Promise<{ generated: boolean }> }}
 *          `generated` is true when this call had to generate the slides
 */
export const useSlideGeneration = () => {
    const { subscribeToMessages } = useRealTimeChat();

    const ensureSlidesGenerated = useCallback(async ({ rfpId, templateId }) => {
        // 1. Already generated? If the check itself fails, carry on with the download.
        try {
            const status = await api.get(`/ppt-config/rfp/${rfpId}/all`);
            if (isPresentationGenerated(status.data)) return { generated: false };
        } catch (error) {
            console.warn('Could not check the presentation status, continuing with the download:', error);
            return { generated: false };
        }

        // 2 and 3. Listen first, so a quick "completed" message is not missed, then start.
        let finish;
        let fail;
        const completion = new Promise((resolve, reject) => {
            finish = resolve;
            fail = reject;
        });
        completion.catch(() => {}); // an early failure is handled below, not as an unhandled rejection

        const unsubscribe = subscribeToMessages((payload) => {
            const type = payload?.type || payload?.event;
            const body = payload?.payload || payload?.data || payload;
            const eventRfpId = body?.rfpId || body?.rfp_id;
            if (String(eventRfpId) !== String(rfpId)) return;

            if (type === 'PPT_GENERATION_COMPLETED') finish();
            if (type === 'PPT_GENERATION_FAILED') {
                fail(new Error(body?.message || 'Failed to generate presentation slides.'));
            }
        });

        try {
            const formData = new FormData();
            formData.append('rfp_id', rfpId);
            if (templateId) formData.append('template_id', templateId);

            const response = await api.post(`ppt-config/rfp/${rfpId}/generate-slides`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Some answers already contain the finished slides; otherwise wait for the websocket.
            const data = response?.data;
            const isDoneAlready = Boolean(data && (data.ppt_generated || data.slide_order || data.slides));
            if (!isDoneAlready) await completion;
            return { generated: true };
        } finally {
            unsubscribe();
        }
    }, [subscribeToMessages]);

    return { ensureSlidesGenerated };
};
